import React from 'react';
import { Card } from '../../../components/ui/card';
import { Sparkles, Lock, Inbox, RefreshCw, Check, Clock3, Ban } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { STATUS_META, OUTCOME_META, ALIGN_META, fmtDate, fmtTime } from './demoConstants';

/* ── Page header ────────────────────────────────────────────────────── */
export const PageHeader = ({ icon: Icon, eyebrow, title, subtitle, children, testid, embedded = false }) => (
  embedded ? (
    <div className="flex flex-wrap items-center justify-between gap-3" data-testid={testid}>
      {subtitle && <p className="text-[12.5px] text-slate-500 max-w-2xl">{subtitle}</p>}
      <div className="flex items-center gap-2 flex-wrap ml-auto">{children}</div>
    </div>
  ) : (
  <Card className="px-5 sm:px-6 py-5 border flex flex-wrap items-center justify-between gap-4" data-testid={testid}>
    <div className="flex items-start gap-3.5">
      {Icon && (
        <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center">
          <Icon className="w-[18px] h-[18px]" />
        </div>
      )}
      <div>
        {eyebrow && <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">{eyebrow}</p>}
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 leading-tight">{title}</h1>
        {subtitle && <p className="text-[13px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2 flex-wrap">{children}</div>
  </Card>
  )
);

/* ── KPI card — identical to the platform's Revenue Report KPI ──────── */
export const Stat = ({ icon: Icon, label, value, sub, tone = 'slate', active, onClick, testid }) => {
  const tc = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
    violet: 'bg-violet-100 text-violet-700',
    sky: 'bg-sky-100 text-sky-700',
  }[tone] || 'bg-slate-100 text-slate-700';
  return (
    <Card
      className={`p-4 border ${onClick ? 'cursor-pointer hover:border-violet-400 hover:shadow-md transition group' : ''} ${active ? 'border-violet-400 ring-1 ring-violet-200' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => { if (onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      data-testid={testid}
    >
      {Icon && <div className={`w-9 h-9 rounded-lg ${tc} flex items-center justify-center`}><Icon className="w-4 h-4" /></div>}
      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium mt-3">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-0.5 tabular-nums">{value}</p>
      {sub ? <p className="text-[10px] text-slate-400 mt-1">{sub}</p>
        : onClick ? <p className={`text-[10px] text-violet-500 transition mt-1 ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>{active ? 'Filtered — click to clear' : 'Click to drill down →'}</p> : null}
    </Card>
  );
};

/* ── Badges ─────────────────────────────────────────────────────────── */
export const StatusPill = ({ status, finalised, voided, notConducted }) => (
  <span className="inline-flex items-center gap-1.5 flex-wrap">
    {voided && (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-slate-200 text-slate-600 border border-slate-300" data-testid="status-void">
        <Ban className="w-2.5 h-2.5" />VOID
      </span>
    )}
    <span className={`${STATUS_META[status]?.cls || STATUS_META.not_done.cls}${voided ? ' line-through opacity-60' : ''}`}>{STATUS_META[status]?.label || status}</span>
    {finalised && (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-slate-900 text-white">
        <Lock className="w-2.5 h-2.5" />LOCKED
      </span>
    )}
    {notConducted?.reason && (
      <span className="block w-full text-[10.5px] text-slate-500 leading-snug" title={`${notConducted.reason} — marked by ${notConducted.by} (${notConducted.by_role}) on ${new Date(notConducted.at).toLocaleString('en-IN')}`} data-testid="status-not-conducted-note">
        {notConducted.reason} · by {notConducted.by}
      </span>
    )}
  </span>
);
export const OutcomePill = ({ outcome }) => (outcome?.result
  ? (
    <span className="inline-flex flex-col gap-0.5">
      <span className={OUTCOME_META[outcome.result]?.cls}>{OUTCOME_META[outcome.result]?.label}</span>
      {outcome.enrollment_no ? <span className="text-[10px] font-mono text-emerald-700">#{outcome.enrollment_no}</span> : null}
    </span>
  )
  : <span className="text-slate-300">—</span>);
export const AlignPill = ({ value }) => (value
  ? <span className={ALIGN_META[value]?.badge}>{ALIGN_META[value]?.dot} {ALIGN_META[value]?.label}</span>
  : <span className="text-slate-300">—</span>);

/* ── AI verdict banner ──────────────────────────────────────────────── */
export const AiVerdictBanner = ({ verdict, overall, onRegenerate, busy }) => {
  if (!verdict?.text) return null;
  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/70 p-4" data-testid="ai-verdict-banner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">
          <Sparkles className="w-3.5 h-3.5" />AI Alignment Verdict
        </span>
        <div className="flex items-center gap-2">
          {overall && <span className="text-[11px] font-medium text-slate-500">{ALIGN_META[overall]?.dot} {ALIGN_META[overall]?.label}</span>}
          {onRegenerate && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={onRegenerate} className="h-7 text-[11px] text-violet-600 hover:bg-violet-100" data-testid="regenerate-verdict-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${busy ? 'animate-spin' : ''}`} />Regenerate
            </Button>
          )}
        </div>
      </div>
      <p className="text-[13.5px] text-slate-800 mt-2 leading-relaxed" data-testid="ai-verdict-text">{verdict.text}</p>
    </div>
  );
};

/* ── Quoted comment block (never merged between roles) ──────────────── */
export const QuoteBlock = ({ author, role, at, text, tone = 'violet', testid }) => (
  <figure className={`rounded-lg bg-slate-50/90 p-3.5 border-l-4 ${tone === 'indigo' ? 'border-indigo-500' : 'border-violet-600'}`} data-testid={testid}>
    <blockquote className="text-[13px] text-slate-700 italic leading-relaxed">“{text}”</blockquote>
    <figcaption className="mt-2 text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">
      {author || '—'} · {role}{at ? ` · ${new Date(at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
    </figcaption>
  </figure>
);

/* ── Empty state ────────────────────────────────────────────────────── */
export const EmptyState = ({ title = 'Nothing here yet', hint, action }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="demo-empty-state">
    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3"><Inbox className="w-5 h-5 text-slate-400" /></div>
    <p className="text-sm font-semibold text-slate-700">{title}</p>
    {hint && <p className="text-[12px] text-slate-400 mt-1 max-w-sm">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/* ── Feedback progress chips ────────────────────────────────────────── */
const FbChip = ({ label, done, mine }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
    done ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : mine ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
    {done ? <Check className="w-2.5 h-2.5" /> : <Clock3 className="w-2.5 h-2.5" />}{label}
  </span>
);

export const FeedbackCell = ({ demo, mySide }) => {
  if (demo.status !== 'approved' && !demo.trainer_feedback && !demo.counsellor_feedback) return <span className="text-slate-300">—</span>;
  const tDone = !!demo.trainer_feedback;
  const cDone = !!demo.counsellor_feedback;
  const mineMissing = (mySide === 'trainer' && !tDone) || (mySide === 'counsellor' && !cDone);
  return (
    <span className="inline-flex flex-col gap-1" data-testid={`fb-cell-${demo.id}`}>
      <span className="inline-flex items-center gap-1">
        <FbChip label="Trainer" done={tDone} mine={mySide === 'trainer'} />
        <FbChip label="Counsellor" done={cDone} mine={mySide === 'counsellor'} />
      </span>
      {mineMissing && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider bg-rose-600 text-white" data-testid={`pending-you-${demo.id}`}>
          Your review missing
        </span>
      )}
    </span>
  );
};

/* ── Demo table (shared by queue + report) ──────────────────────────── */
export const DemoTable = ({ rows, onRowClick, showCounsellor = true, showTrainer = true, mySide }) => {
  return (
    <Card className="border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500 font-medium border-b border-slate-200">
              <th className="px-4 py-3">Demo</th>
              <th className="px-4 py-3">When</th>
              {showTrainer && <th className="px-4 py-3">Trainer</th>}
              {showCounsellor && <th className="px-4 py-3">Counsellor</th>}
              <th className="px-4 py-3">Course</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Feedback</th>
              <th className="px-4 py-3">Outcome</th>
              <th className="px-4 py-3">Alignment</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr
                key={d.id}
                onClick={() => onRowClick?.(d)}
                className="border-b border-slate-100 last:border-0 hover:bg-violet-50 transition-colors cursor-pointer"
                data-testid={`demo-row-${d.id}`}
              >
                <td className="px-4 py-3">
                  <span className="font-semibold text-slate-800 font-mono text-[12px]">{d.demo_code}</span>
                  {d.student_name ? <span className="block text-[11px] text-slate-400">{d.student_name}</span> : null}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtDate(d.demo_date)}<span className="text-slate-400"> · {fmtTime(d.demo_time)}</span></td>
                {showTrainer && <td className="px-4 py-3 text-slate-700">{d.trainer_name}</td>}
                {showCounsellor && <td className="px-4 py-3 text-slate-700">{d.counsellor_name}</td>}
                <td className="px-4 py-3 text-slate-700">{d.course_name}</td>
                <td className="px-4 py-3 min-w-[180px]"><StatusPill status={d.status} finalised={d.finalised} voided={d.void} notConducted={d.not_conducted} /></td>
                <td className="px-4 py-3"><FeedbackCell demo={d} mySide={mySide} /></td>
                <td className="px-4 py-3"><OutcomePill outcome={d.outcome} /></td>
                <td className="px-4 py-3"><AlignPill value={d.feedback_alignment} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export const PreDemoFlag = ({ r }) => {
  if (r.invoice_credited_to) return (
    <span className="block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 w-fit" data-testid={`flag-shared-${r.id}`}>Invoice already credited to {r.invoice_credited_to}</span>
  );
  if (r.pre_demo_days !== null && r.pre_demo_days !== undefined) return (
    <span className="block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200 w-fit" title="Invoice is dated before the demo — counted because it is within 15 days (demo may have been logged late). Please verify." data-testid={`flag-predemo-${r.id}`}>Invoice {r.pre_demo_days}d before demo — verify</span>
  );
  if (r.earlier_invoice) return (
    <span className="block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 w-fit" title="Older than 15 days before the demo — not counted. A manager can pick it if it is the admission invoice." data-testid={`flag-earlier-${r.id}`}>{r.earlier_invoice.invoice_number} dated {r.earlier_invoice.date} ({r.earlier_invoice.days_before}d before demo) — not counted</span>
  );
  return null;
};
